import * as React from 'react';
import { useQuery } from '@tanstack/react-query';
import { Link, useLocation } from 'react-router-dom';
import { ChevronRight } from 'lucide-react';

import {
    ADMIN_HOME_PATH,
    buildAdminBreadcrumbs,
    getAdminCompanyBreadcrumbLabel,
    isAdminDetailRoute,
    readAdminOrgId,
    type AdminBreadcrumbItem,
    type AdminFromCompanyDetailState,
} from '../lib/adminBreadcrumbs';
import { useAccessApi } from '../services/api';
import { queryKeys } from '../hooks/useApi';

export type { AdminFromCompanyDetailState } from '../lib/adminBreadcrumbs';

function parseNumericId(value: string | undefined): number | null {
    return value && /^\d+$/.test(value) ? Number(value) : null;
}

function BreadcrumbSeparator() {
    return <ChevronRight className="mx-1.5 size-4 shrink-0 text-neutral-400" aria-hidden />;
}

function BreadcrumbLink({ crumb, isCurrent }: { crumb: AdminBreadcrumbItem; isCurrent: boolean }) {
    if (isCurrent) {
        return (
            <span
                aria-current="page"
                className="inline-flex min-h-6 min-w-0 items-center font-medium text-neutral-900"
            >
                <span className="break-words">{crumb.label}</span>
            </span>
        );
    }

    return (
        <Link
            to={crumb.path}
            {...(crumb.state !== undefined ? { state: crumb.state } : {})}
            className="inline-flex min-h-6 min-w-0 max-w-[16rem] items-center transition-colors duration-200 hover:text-primary-500"
            title={crumb.label}
        >
            <span className="truncate">{crumb.label}</span>
        </Link>
    );
}

function BreadcrumbTrail({ breadcrumbs }: { breadcrumbs: AdminBreadcrumbItem[] }) {
    return (
        <React.Fragment>
            <nav
                aria-label="Breadcrumb"
                className="flex w-full flex-wrap items-center gap-y-1 pt-4 text-sm text-neutral-600"
            >
                {breadcrumbs.map((crumb, index) => (
                    <div key={`${crumb.path}-${index}`} className="flex min-w-0 items-center">
                        {index > 0 ? <BreadcrumbSeparator /> : null}
                        <BreadcrumbLink
                            crumb={crumb}
                            isCurrent={index === breadcrumbs.length - 1}
                        />
                    </div>
                ))}
            </nav>
        </React.Fragment>
    );
}

export function Breadcrumbs() {
    const location = useLocation();
    const { getOrganizationById, getOrganizations } = useAccessApi();

    const pathnames = location.pathname.split('/').filter((x) => x);
    const shouldHideBreadcrumbs = location.pathname === '/' || location.pathname === '/admin';
    const adminIndex = pathnames.indexOf('admin');
    const orgId = readAdminOrgId(pathnames, adminIndex);

    const { data: organization } = useQuery({
        queryKey: ['organization', orgId],
        queryFn: () => {
            if (!orgId) return Promise.resolve(null);
            return getOrganizationById(orgId).catch(() => null);
        },
        enabled: !!orgId,
        staleTime: 5 * 60 * 1000,
    });

    const navState = location.state as AdminFromCompanyDetailState | null | undefined;
    const fromCompanyDetail = navState?.fromCompanyDetail;
    const fromSowDetail = navState?.fromSowDetail;
    const adminCompanyDetail = navState?.adminCompanyDetail;
    const isAdminSowDetail = isAdminDetailRoute(pathnames, adminIndex, 'sows');
    const isAdminPricingConfigDetail = isAdminDetailRoute(pathnames, adminIndex, 'pricing-configs');

    const adminCompaniesDetailNumericId =
        adminIndex >= 0 && pathnames[adminIndex + 1] === 'companies'
            ? parseNumericId(pathnames[adminIndex + 2])
            : null;

    const fromCoCompanyNumericId = parseNumericId(fromCompanyDetail?.companyId);
    const fromSowCompanyNumericId = parseNumericId(fromSowDetail?.companyId);

    const organizationsLookupNumericId =
        adminCompaniesDetailNumericId ??
        (isAdminSowDetail ? fromCoCompanyNumericId : null) ??
        (isAdminPricingConfigDetail && fromSowCompanyNumericId !== null
            ? fromSowCompanyNumericId
            : null);

    const { data: creditPortalOrganizations } = useQuery({
        queryKey: [...queryKeys.all, 'organizations'],
        queryFn: getOrganizations,
        enabled:
            organizationsLookupNumericId != null &&
            Number.isFinite(organizationsLookupNumericId) &&
            organizationsLookupNumericId > 0,
        staleTime: 5 * 60 * 1000,
    });

    const breadcrumbs = buildAdminBreadcrumbs({
        pathnames,
        portalHomePath: ADMIN_HOME_PATH,
        organizationLabel: organization?.display_name || 'Organization',
        adminCompanyDetail,
        fromCompanyDetail,
        fromSowDetail,
        getAdminCompanyLabel: (numericCompanyId) =>
            getAdminCompanyBreadcrumbLabel(creditPortalOrganizations, numericCompanyId),
    });

    if (shouldHideBreadcrumbs || breadcrumbs.length <= 1) {
        return null;
    }

    return <BreadcrumbTrail breadcrumbs={breadcrumbs} />;
}
